import { DatePicker, Input, Select, type Column } from "@legal-500/legal500-design-system";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  marketingDownloadAudit,
  type DownloadAuditEntry,
} from "@/api/marketingDownloadAudit.service";
import { DataTable } from "@/components/DataTable/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { PaginationControls } from "@/components/PaginationControls";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import styles from "./DownloadAudit.module.css";

const PAGE_SIZE = 50;

const EMBARGO_OPTIONS = [
  { value: "", label: "Everything taken" },
  { value: "true", label: "Taken under embargo" },
];

/** How each artefact reads to somebody who did not build the toolkit. */
const ARTEFACT_LABELS: Record<string, string> = {
  badge: "Badge",
  copy_document: "Copy document",
  badges: "Bulk: all badges",
  copy: "Bulk: all copy",
  summary: "Bulk: ranking summary",
  everything: "Bulk: everything",
};

/** Turn a yyyy-MM-dd filter into a bound covering the whole of that day. */
function dateToIso(date: string | undefined, edge: "start" | "end"): string | undefined {
  if (!date) return undefined;
  const suffix = edge === "start" ? "T00:00:00.000Z" : "T23:59:59.999Z";
  const parsed = new Date(`${date}${suffix}`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

export function DownloadAuditPage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("staff.marketing.read");
  const [searchParams, setSearchParams] = useSearchParams();

  const firmRef = searchParams.get("firmRef") ?? "";
  const embargo = searchParams.get("embargo") ?? "";
  const fromDate = searchParams.get("from") ?? "";
  const toDate = searchParams.get("to") ?? "";
  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const offset = (page - 1) * PAGE_SIZE;

  const filters = useMemo(
    () => ({
      firmRef: firmRef || undefined,
      preReleaseOnly: embargo === "true",
      takenFrom: dateToIso(fromDate || undefined, "start"),
      takenTo: dateToIso(toDate || undefined, "end"),
      limit: PAGE_SIZE,
      offset,
    }),
    [firmRef, embargo, fromDate, toDate, offset],
  );

  // The same hook the copy-template and terms panels use, so all three Marketing Toolkit
  // pages fetch the same way. The filters are the dependency list: changing one refetches.
  const { data, loading, error } = useQuery(() => marketingDownloadAudit.list(filters), [filters]);

  function setParam(key: string, value: string | null) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      return next;
    });
  }

  const rows = data?.entries ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns: Column<DownloadAuditEntry>[] = [
    {
      header: "When",
      accessor: "takenAt",
      render: (_value, row) => (
        <span className={styles.when}>{new Date(row.takenAt).toLocaleString()}</span>
      ),
    },
    {
      header: "Taken by",
      accessor: "userEmail",
      render: (_value, row) => (
        <span className={styles.taker}>
          <span className={styles.takerEmail}>{row.userEmail}</span>
          <span className={styles.takerFirm}>{row.firmRef}</span>
        </span>
      ),
    },
    {
      header: "Asset",
      accessor: "assetName",
      render: (_value, row) => (
        <span className={styles.taker}>
          <span className={styles.assetName}>{row.assetName ?? row.assetId}</span>
          <span className={styles.assetId}>{row.assetKind}</span>
        </span>
      ),
    },
    {
      header: "Taken as",
      accessor: "artefact",
      render: (_value, row) => (
        <span>
          {ARTEFACT_LABELS[row.artefact] ?? row.artefact}
          {row.format ? ` (${row.format})` : ""}
        </span>
      ),
    },
    {
      header: "Embargo",
      accessor: "preRelease",
      render: (_value, row) =>
        row.preRelease ? (
          <span className={styles.embargoPill}>Under embargo</span>
        ) : (
          <span className={styles.muted}>—</span>
        ),
    },
    {
      header: "From",
      accessor: "ipAddress",
      render: (_value, row) => <span className={styles.ip}>{row.ipAddress ?? "—"}</span>,
    },
  ];

  if (!canRead) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Download audit"
        subtitle="Every asset a firm has taken from the Marketing Toolkit, newest first."
      />

      <div className={styles.filters}>
        <div className={styles.filterCell}>
          <Input
            label="Firm"
            placeholder="Firm reference"
            value={firmRef}
            onChange={e => setParam("firmRef", e.target.value || null)}
          />
        </div>
        <div className={styles.filterCell}>
          <Select
            label="Embargo"
            value={embargo}
            options={EMBARGO_OPTIONS}
            onChange={e => setParam("embargo", e.target.value || null)}
            size="small"
          />
        </div>
        <div className={styles.filterCell}>
          <DatePicker
            label="From"
            value={fromDate}
            onChange={e => setParam("from", e.target.value || null)}
          />
        </div>
        <div className={styles.filterCell}>
          <DatePicker
            label="To"
            value={toDate}
            onChange={e => setParam("to", e.target.value || null)}
          />
        </div>
      </div>

      <p className={styles.muted}>
        Entries are written when an asset is served and are never changed or removed. Copying a
        section to the clipboard happens in the browser, so it cannot appear here.
      </p>

      <div className={styles.contentSection}>
        {loading ? (
          <p role="status" className={styles.muted}>
            Loading the download audit…
          </p>
        ) : error ? (
          <p role="alert" className={styles.errorBanner}>
            {error.message}
          </p>
        ) : rows.length === 0 ? (
          <p className={styles.emptyState}>Nothing has been taken that matches these filters.</p>
        ) : (
          <>
            <DataTable data={rows} columns={columns} rowKey="id" />
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              startIndex={offset}
              endIndex={offset + rows.length}
              totalCount={total}
              itemName="downloads"
              onPageChange={p => setParam("page", String(p))}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default DownloadAuditPage;
